import getSignedUrl from '@lib/imgproxy/getSignedUrl';

import { generatePublicContentUrl } from './generatePublicContentUrl';

export type ResourceType = 'image' | 'gif' | 'video';

/**
 * Описание static media-ресурса в S3.
 *
 * @property path - Путь внутри S3-бакета. Начальный слэш можно не указывать.
 * @property type - Тип ресурса. Указывайте явно, если нужно грузить gif или video.
 * @property quality - Качество для imgproxy. Для публичных gif/video ресурсов игнорируется.
 */
export interface ResourceUrlConfig {
  path: string;
  type?: ResourceType;
  quality?: number;
}

const PUBLIC_RESOURCE_EXTENSIONS = ['.gif', '.mp4'];

/**
 * Нормализует путь для imgproxy.
 *
 * @param path - Путь внутри S3-бакета.
 * @returns Путь с начальным слэшем, который ожидает imgproxy.
 */
const withLeadingSlash = (path: string) =>
  path.startsWith('/') ? path : `/${path}`;

/**
 * Нормализует путь для public storage.
 *
 * @param path - Путь внутри S3-бакета.
 * @returns Bucket-relative путь без начального слэша.
 */
const withoutLeadingSlash = (path: string) =>
  path.startsWith('/') ? path.slice(1) : path;

/**
 * Проверяет, нужно ли отдавать ресурс напрямую из public storage.
 *
 * @param path - Путь к ресурсу.
 * @param type - Тип ресурса, если он задан явно.
 * @returns true для gif/video ресурсов, которые не должны проходить через imgproxy.
 */
const isPublicResource = (path: string, type?: ResourceType) => {
  if (type) {
    return type === 'gif' || type === 'video';
  }

  const normalizedPath = path.toLowerCase();

  return PUBLIC_RESOURCE_EXTENSIONS.some((extension) =>
    normalizedPath.endsWith(extension),
  );
};

/**
 * Формирует URL для статичных media-ресурсов:
 * - images подписываются через imgproxy и могут использовать width/height/quality;
 * - gifs и videos отдаются из public storage как есть.
 *
 * @param config - Описание ресурса и опции imgproxy.
 * @returns Подписанный imgproxy URL для image или публичный URL для gif/video.
 */
export const generateResourceUrl = async ({
  path,
  type,
  quality,
}: ResourceUrlConfig): Promise<string> => {
  if (isPublicResource(path, type)) {
    return generatePublicContentUrl(withoutLeadingSlash(path));
  }

  return getSignedUrl(withLeadingSlash(path), undefined, undefined, quality);
};

/**
 * Формирует URL для карты ресурсов, сохраняя исходные ключи.
 *
 * @param sources - Карта ресурсов, где ключи используются в результирующем объекте.
 * @returns Карта URL с теми же ключами, что и в sources.
 */
export const generateResourceUrls = async <
  T extends Record<string, ResourceUrlConfig>,
>(
  sources: T,
): Promise<{ [K in keyof T]: string }> => {
  const result = {} as { [K in keyof T]: string };

  await Promise.all(
    Object.entries(sources).map(async ([key, source]) => {
      result[key as keyof T] = await generateResourceUrl(source);
    }),
  );

  return result;
};
